import React from 'react';
import { Badge } from '../ui/badge';
import UserAvatar from '../UserAvatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import {
  MoreVertical,
  Pencil,
  Trash2,
  Eye,
  User,
  Mail,
  Phone,
  MapPin,
  FileText,
  ClipboardList,
  Users,
  Brain,
  Briefcase,
  Upload,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { format } from 'date-fns';

const CandidateTable = ({
  candidates,
  onView,
  onEdit,
  onDelete,
  onViewResume,
  onUploadResume,
  onAddToWorkbench,
  onFindSimilar,
  onAISummary,
  onJobRecommendations,
  canDelete,
  user,
  // iter_39: bulk selection
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  // Column visibility — Set of hidden column keys. Optional; defaults to none.
  hiddenCols,
  // Optional density className (applies tighter padding via Tailwind child
  // selectors when "compact" is chosen). Passed through from the page.
  densityClass,
}) => {
  const isCol = (key) => !(hiddenCols && hiddenCols.has(key));
  const selectionEnabled =
    typeof onToggleSelect === 'function' && typeof onToggleSelectAll === 'function';
  // Number of visible cells per row — used for the empty-state colSpan.
  const baseVisibleCols =
    1 + // Candidate (anchor, required)
    1 + // Actions (anchor, required)
    ['contact', 'experience', 'skills', 'source', 'uploaded_by', 'added', 'last_edited']
      .filter((k) => isCol(k)).length +
    (selectionEnabled ? 1 : 0);
  const visibleIds = (candidates || []).map((c) => c?.id).filter(Boolean);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds?.has?.(id));
  const someVisibleSelected =
    visibleIds.some((id) => selectedIds?.has?.(id)) && !allVisibleSelected;
  if (!candidates || candidates.length === 0) {
    return (
      <Table>
        <TableHeader>
          <TableRow>
            {selectionEnabled && <TableHead className="w-[40px]"></TableHead>}
            <TableHead>Candidate</TableHead>
            {isCol('contact') && <TableHead>Contact</TableHead>}
            {isCol('experience') && <TableHead>Experience</TableHead>}
            {isCol('skills') && <TableHead>Skills</TableHead>}
            {isCol('source') && <TableHead>Source</TableHead>}
            {isCol('uploaded_by') && <TableHead>Uploaded by</TableHead>}
            {isCol('added') && <TableHead>Added</TableHead>}
            {isCol('last_edited') && <TableHead>Last edited</TableHead>}
            <TableHead className="w-[50px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow>
            <TableCell colSpan={baseVisibleCols} className="text-center py-8 text-slate-500">
              No candidates found
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    );
  }

  return (
    <Table
      containerClassName="max-h-[calc(100vh-280px)] min-h-[360px]"
      className={densityClass}
      data-testid="candidates-table"
    >
      <TableHeader className="sticky top-0 z-10 bg-white shadow-sm">
        <TableRow>
          {selectionEnabled && (
            <TableHead className="w-[40px]">
              <Checkbox
                checked={allVisibleSelected}
                // Indeterminate state when some-but-not-all visible rows are selected
                data-state={someVisibleSelected ? 'indeterminate' : undefined}
                onCheckedChange={(v) => onToggleSelectAll(!!v, visibleIds)}
                aria-label="Select all visible candidates"
                data-testid="candidates-select-all-checkbox"
              />
            </TableHead>
          )}
          <TableHead>Candidate</TableHead>
          {isCol('contact') && <TableHead>Contact</TableHead>}
          {isCol('experience') && <TableHead>Experience</TableHead>}
          {isCol('skills') && <TableHead>Skills</TableHead>}
          {isCol('source') && <TableHead>Source</TableHead>}
          {isCol('uploaded_by') && <TableHead>Uploaded by</TableHead>}
          {isCol('added') && <TableHead>Added</TableHead>}
          {isCol('last_edited') && <TableHead>Last edited</TableHead>}
          <TableHead className="w-[50px]"></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {candidates.map((candidate) => {
          const isSelected = !!selectedIds?.has?.(candidate?.id);
          return (
          <TableRow
            key={candidate?.id || Math.random()}
            className={`table-row-hover ${isSelected ? 'bg-teal-50/40' : ''}`}
            data-testid={`candidate-row-${candidate?.id}`}
            data-selected={isSelected ? 'true' : undefined}
          >
            {selectionEnabled && (
              <TableCell className="pr-0">
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => onToggleSelect(candidate?.id)}
                  onClick={(e) => e.stopPropagation()}
                  aria-label={`Select ${candidate?.name || 'candidate'}`}
                  data-testid={`candidate-row-checkbox-${candidate?.id}`}
                />
              </TableCell>
            )}
            <TableCell>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-teal-700" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-slate-900">
                      {candidate?.name || 'Unnamed Candidate'}
                    </p>
                    {(candidate?.resume_url || candidate?.gridfs_resume_id) ? (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700" title="Resume available">
                        <FileText className="w-3 h-3 mr-0.5" />
                        CV
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-500" title="No resume uploaded">
                        No CV
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-500">
                    {candidate?.current_designation || candidate?.current_company || '-'}
                  </p>
                </div>
              </div>
            </TableCell>
            {isCol('contact') && (
              <TableCell>
                <div className="space-y-1">
                  {candidate?.email && (
                    <div className="flex items-center gap-1 text-sm text-slate-600">
                      <Mail className="w-3 h-3" /> {candidate.email}
                    </div>
                  )}
                  {candidate?.phone && (
                    <div className="flex items-center gap-1 text-sm text-slate-600">
                      <Phone className="w-3 h-3" /> {candidate.phone}
                    </div>
                  )}
                </div>
              </TableCell>
            )}
            {isCol('experience') && (
              <TableCell>
                <div>
                  <p className="font-medium">{candidate?.experience_years ?? 0} yrs</p>
                  {candidate?.current_location && (
                    <p className="text-sm text-slate-500 flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {candidate.current_location}
                    </p>
                  )}
                </div>
              </TableCell>
            )}
            {isCol('skills') && (
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  {candidate?.skills?.slice(0, 3).map((skill, i) => (
                    <Badge key={i} variant="outline" className="text-xs">
                      {skill}
                    </Badge>
                  ))}
                  {(candidate?.skills?.length || 0) > 3 && (
                    <Badge variant="outline" className="text-xs">
                      +{(candidate?.skills?.length || 0) - 3}
                    </Badge>
                  )}
                </div>
              </TableCell>
            )}
            {isCol('source') && (
              <TableCell>
                <Badge variant="outline" className="capitalize">
                  {(candidate?.source || 'manual').replace('_', ' ')}
                </Badge>
              </TableCell>
            )}
            {isCol('uploaded_by') && (
              <TableCell className="text-slate-600 text-sm">
                {candidate?.created_by_name ? (
                  <span className="flex items-center gap-1.5" data-testid={`candidate-uploaded-by-${candidate?.id}`}>
                    <UserAvatar name={candidate.created_by_name} size="xs" />
                    <span className="truncate">{candidate.created_by_name}</span>
                  </span>
                ) : (
                  <span data-testid={`candidate-uploaded-by-${candidate?.id}`}>-</span>
                )}
              </TableCell>
            )}
            {isCol('added') && (
              <TableCell className="text-slate-500">
                {candidate?.created_at
                  ? format(new Date(candidate.created_at), 'MMM dd, yyyy')
                  : '-'}
              </TableCell>
            )}
            {isCol('last_edited') && (
              <TableCell className="text-slate-600 text-sm" data-testid={`candidate-last-edited-${candidate?.id}`}>
                {candidate?.updated_at ? (
                  <div className="flex flex-col gap-0.5">
                    <span className="text-slate-700">
                      {format(new Date(candidate.updated_at), 'MMM dd, yyyy')}
                    </span>
                    {candidate?.updated_by_name ? (
                      <span className="flex items-center gap-1 text-xs text-slate-500">
                        <UserAvatar name={candidate.updated_by_name} size="xs" />
                        <span className="truncate max-w-[120px]">{candidate.updated_by_name}</span>
                      </span>
                    ) : null}
                  </div>
                ) : (
                  <span className="text-slate-400">—</span>
                )}
              </TableCell>
            )}
            <TableCell>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    data-testid={`candidate-menu-${candidate?.id}`}
                  >
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onView(candidate)}>
                    <Eye className="w-4 h-4 mr-2" /> View
                  </DropdownMenuItem>
                  {(candidate?.resume_url || candidate?.gridfs_resume_id) && (
                    <DropdownMenuItem onClick={() => onViewResume(candidate)}>
                      <FileText className="w-4 h-4 mr-2" /> View Resume
                    </DropdownMenuItem>
                  )}
                  {onUploadResume && user?.role !== 'client_spoc' && (
                    <DropdownMenuItem onClick={() => onUploadResume(candidate)}>
                      <Upload className="w-4 h-4 mr-2 text-teal-600" /> 
                      <span className="text-teal-700">
                        {(candidate?.resume_url || candidate?.gridfs_resume_id) ? 'Replace Resume' : 'Upload Resume'}
                      </span>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => onEdit(candidate)}>
                    <Pencil className="w-4 h-4 mr-2" /> Edit
                  </DropdownMenuItem>
                  {user?.role !== 'client_spoc' && (
                    <DropdownMenuItem onClick={() => onAddToWorkbench(candidate)}>
                      <ClipboardList className="w-4 h-4 mr-2" /> Add to Workbench
                    </DropdownMenuItem>
                  )}
                  {onFindSimilar && (
                    <DropdownMenuItem onClick={() => onFindSimilar(candidate)}>
                      <Users className="w-4 h-4 mr-2" /> Find Similar
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  {/* AI Features */}
                  {onAISummary && (
                    <DropdownMenuItem onClick={() => onAISummary(candidate)}>
                      <Brain className="w-4 h-4 mr-2 text-purple-600" />
                      <span className="text-purple-700">AI Summary</span>
                    </DropdownMenuItem>
                  )}
                  {onJobRecommendations && (
                    <DropdownMenuItem onClick={() => onJobRecommendations(candidate)}>
                      <Briefcase className="w-4 h-4 mr-2 text-blue-600" />
                      <span className="text-blue-700">Job Recommendations</span>
                    </DropdownMenuItem>
                  )}
                  {canDelete && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => onDelete(candidate)}
                        className="text-red-600"
                      >
                        <Trash2 className="w-4 h-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </TableCell>
          </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default CandidateTable;
